import { useEffect, useMemo, useRef, useState } from 'react';
import { MY_TRAINING_LOGS_PAGE } from 'constants/pages';
import {
  FileFormatEnum,
  LessonReflectionCommentTypesEnum,
  LessonReflectionFiles,
  LessonSkillsEnum,
} from 'types/generated/client';
import { toTitleCase } from 'utils/shared/toTitleCase';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import Calendar from 'svg/Calendar';
import ChevronUp from 'svg/ChevronUp';
import CloseIcon from 'svg/CloseIcon';
import Button, { ButtonLink, ButtonText } from 'components/Button';
import Pill from 'components/Pill';
import { formatCreationWithIntl } from 'components/Premium/utils';
import { getSkillLabel } from 'components/SkillAnalysis/utils';
import Modal from 'components/modals/Modal';
import { RecapDetailModalProps, SkillBoxProps } from '../props';

const RecapDetailModal: React.FC<RecapDetailModalProps> = ({
  isOpen,
  handleClose,
  handleBookAnotherLesson,
  recap,
}) => {
  const { completedSkills, assignmentSkills, upcomingSkills } = useMemo(() => {
    return (
      recap.lessonReflectionComments?.reduce(
        (acc, comment) => {
          const skill = getSkillLabel(comment.skill || comment.customSkill || '');

          if (!skill) return acc;

          switch (comment.type) {
            case LessonReflectionCommentTypesEnum.Completed:
              acc.completedSkills.push(skill);
              break;
            case LessonReflectionCommentTypesEnum.Assignment:
              acc.assignmentSkills.push(skill);
              break;
            case LessonReflectionCommentTypesEnum.Upcoming:
              acc.upcomingSkills.push(skill);
              break;
          }

          return acc;
        },
        {
          completedSkills: [] as (LessonSkillsEnum | string)[],
          assignmentSkills: [] as (LessonSkillsEnum | string)[],
          upcomingSkills: [] as (LessonSkillsEnum | string)[],
        },
      ) || {}
    );
  }, [recap.lessonReflectionComments]);

  return (
    <Modal isOpen={isOpen} handleClose={handleClose} classNameMaxWidth="max-w-[40rem]">
      <div className="flex items-center justify-between border-color-border-input-lightmode px-ds-3xl py-5 dark:border-color-border-input-darkmode max-sm:px-ds-lg sm:border-b">
        <h2 className="typography-product-heading-desktop max-sm:typography-product-heading-mobile">
          Lesson recap
        </h2>
        <ButtonText onClick={handleClose}>
          <CloseIcon className="h-6 w-6 max-sm:h-5 max-sm:w-5" />
        </ButtonText>
      </div>
      <div className="px-ds-3xl pb-ds-2xl pt-ds-xl max-sm:px-ds-lg max-sm:pt-ds-lg">
        <div className="flex items-center gap-ds-md max-sm:gap-ds-sm">
          <img
            src={getProfileImageUrlOrPlaceholder({
              path: recap.lessonCoach?.profileImagePath || '',
            })}
            className="h-[3.75rem] w-[3.75rem] rounded-full bg-white object-cover object-top max-sm:h-8 max-sm:w-8"
            alt="coach-avatar"
          />
          <div className="flex flex-col gap-1">
            <span className="max-sm:typography-product-card-title-mobile typography-product-card-title-desktop">
              {toTitleCase(recap.lessonCoach?.fullName || '')}
            </span>
            <div className="flex items-center gap-2 text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
              <Calendar className="h-4 w-4 max-sm:h-3 max-sm:w-3" />
              <span className="typography-product-text-card-desktop max-sm:typography-product-text-card-mobile ">
                {formatCreationWithIntl(recap.lesson?.times?.[0]?.startDateTime)}
              </span>
            </div>
          </div>
        </div>
        <h3 className="typography-product-heading-compact-desktop max-sm:typography-product-subheading mt-ds-lg">
          Skills practiced
        </h3>
        <div className="mt-3 flex gap-ds-md">
          {completedSkills.map((completedSkill, index) => (
            <Pill size="sm" text={completedSkill} key={index} />
          ))}
        </div>
        <div className="mt-4 flex flex-col gap-2">
          <span className="typography-product-body-highlight max-sm:typography-product-chips-filters">
            Key Takeaway
          </span>
          <span className="max-sm:typography-product-text-card-mobile text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            {recap.details}
          </span>
        </div>
        <div className="flex flex-col">
          <FilePreview files={recap.files as LessonReflectionFiles[]} />
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 max-sm:grid-cols-1">
          <SkillBox label="Skills to practice before the next lesson" skills={assignmentSkills} />
          <SkillBox label="Skills we'll work on next lesson" skills={upcomingSkills} />
        </div>
      </div>
      <div className="flex gap-ds-xl border-t border-color-border-input-lightmode px-ds-3xl py-5 dark:border-color-border-input-darkmode max-sm:flex-col max-sm:gap-ds-md max-sm:px-ds-lg">
        <ButtonLink
          href={MY_TRAINING_LOGS_PAGE}
          sizeDesktop="lg"
          sizeMobile="lg"
          variant="secondary"
        >
          Go to my Training Logs
        </ButtonLink>
        <Button
          sizeDesktop="lg"
          onClick={() => handleBookAnotherLesson(recap.lessonCoach!.id as string)}
          sizeMobile="lg"
          variant="primary"
        >
          Book next lesson
        </Button>
      </div>
    </Modal>
  );
};

export default RecapDetailModal;

const SkillBox: React.FC<SkillBoxProps> = ({ skills, label }) => (
  <div className="flex flex-col rounded-3xl border border-color-border-card-lightmode p-ds-lg shadow-[0px_4px_16px_0px_rgba(0,0,0,0.04)] dark:border-color-border-card-darkmode">
    <span className="max-sm:typography-product-chips-filters typography-product-element-label text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
      {label}
    </span>
    <div className="mt-ds-md flex flex-wrap gap-ds-sm max-sm:mt-ds-sm">
      {skills.map((skill, index) => (
        <Pill text={skill} key={index} type="brand" size="sm" />
      ))}
    </div>
  </div>
);

const FilePreview: React.FC<{
  files: LessonReflectionFiles[];
}> = ({ files }) => {
  const audios = files.filter((file) => file.fileFormat === FileFormatEnum.Audio);
  const otherMedia = files.filter((file) => file.fileFormat !== FileFormatEnum.Audio);

  return (
    <div className="mt-ds-md flex flex-col gap-ds-md">
      <div className="grid grid-cols-4 gap-2  max-sm:grid-cols-3 max-xs:grid-cols-2">
        {otherMedia.map((media, index) => {
          const isVideo = media.fileFormat === FileFormatEnum.Video;
          return isVideo ? (
            <video
              key={index}
              src={media.url}
              className="h-[7.8rem] w-full rounded-md border border-color-border-input-lightmode object-cover dark:border-color-border-input-darkmode"
              controls
            />
          ) : (
            <img
              key={index}
              src={media.url}
              className="h-[7.8rem] w-full rounded-md border border-color-border-input-lightmode object-cover object-top dark:border-color-border-input-darkmode"
            />
          );
        })}
      </div>
      <div className="flex flex-col gap-2">
        {audios.map((audio, index) => (
          <div key={index} className="flex flex-col gap-4">
            <audio src={audio.url} controls className="w-full max-sm:h-10" />
            <div className="flex flex-col gap-2">
              <span className="typography-product-body-highlight max-sm:typography-product-chips-filters">
                Audio Transcript
              </span>
              <TranscriptDisplay transcript={audio.transcript} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const TranscriptDisplay: React.FC<{ transcript: string }> = ({ transcript }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldTruncate, setShouldTruncate] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const lineHeight = parseInt(window.getComputedStyle(textRef.current).lineHeight);
        const height = textRef.current.scrollHeight;
        const lines = Math.ceil(height / lineHeight);
        setShouldTruncate(lines > 3);
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [transcript]);

  return (
    <div>
      <div
        ref={textRef}
        className={`break-words text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary ${
          shouldTruncate && !isExpanded ? 'line-clamp-3' : ''
        }`}
      >
        {transcript}
      </div>
      {shouldTruncate && (
        <div className="mt-2 flex justify-end">
          <ButtonText
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-1"
          >
            {isExpanded ? 'Read less' : 'Read all'}
            <ChevronUp
              className={`h-4 w-4 transition-transform ${isExpanded ? '' : 'rotate-180'}`}
            />
          </ButtonText>
        </div>
      )}
    </div>
  );
};
